import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { ClipboardCheck, ChevronRight } from 'lucide-react';
import { StatusBadge, DAILY_OUTCOME_META, fmtDate } from './fleetUtils';
import DailyCheckDialog from './DailyCheckDialog';
import DailyHistoryView from './DailyHistoryView';

const Row = ({ v, action, badge }) => (
  <Card data-testid={`daily-row-${v.vehicle_id}`}>
    <CardContent className="p-3 flex flex-wrap items-center justify-between gap-2">
      <div>
        <p className="font-mono font-bold">{v.plate_number} <span className="font-sans font-normal text-sm text-gray-500">{v.brand} {v.model}</span></p>
        <p className="text-xs text-gray-500">
          {v.latest_validated_at
            ? `Dernier contrôle quotidien : ${fmtDate(v.latest_validated_at)} par ${v.latest_by || ''} (tentative ${v.attempts_count})`
            : 'Aucun contrôle quotidien aujourd\'hui'}
          {v.recheck_requested && <span className="text-purple-700 font-medium"> — Re-contrôle demandé par le chef de parc</span>}
        </p>
      </div>
      <div className="flex items-center gap-2">
        <StatusBadge status={v.operational_status} />
        {badge}
        {action}
      </div>
    </CardContent>
  </Card>
);

const DecisionDialog = ({ vehicle, open, onClose, onDone }) => {
  const [action, setAction] = useState(null);
  const [justif, setJustif] = useState('');
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { if (open) { setAction(null); setJustif(''); setError(null); } }, [open]);
  if (!vehicle) return null;

  const submit = async () => {
    setSubmitting(true); setError(null);
    try {
      await axios.post(`${API}/fleet/daily/security-decision/${vehicle.vehicle_id}`, {
        action, justification: justif || null,
      }, { withCredentials: true });
      onDone?.();
      onClose();
    } catch (e) {
      setError(e.response?.data?.detail || 'Erreur');
    } finally {
      setSubmitting(false);
    }
  };

  const options = [
    { key: 'confirm_immobilization', label: '⛔ Confirmer l\'immobilisation', desc: 'Le véhicule passe en Immobilisé (flux réparation habituel).' },
    { key: 'requalify', label: '🟠 Requalifier en non bloquant', desc: 'Justification obligatoire. Le véhicule redevient utilisable (À surveiller).' },
    { key: 'request_recheck', label: '🔁 Demander un nouveau contrôle', desc: 'Le véhicule reste bloqué tant qu\'un re-contrôle favorable n\'est pas validé.' },
  ];

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent aria-describedby={undefined} data-testid="security-decision-dialog">
        <DialogHeader><DialogTitle>Décision sécurité — {vehicle.plate_number}</DialogTitle></DialogHeader>
        <div className="space-y-2">
          {options.map(o => (
            <button key={o.key} type="button" onClick={() => setAction(o.key)}
              className={`w-full text-left border rounded-lg p-3 ${action === o.key ? 'border-blue-600 bg-blue-50' : 'hover:bg-gray-50'}`}
              data-testid={`decision-${o.key}`}>
              <p className="text-sm font-medium">{o.label}</p>
              <p className="text-xs text-gray-500">{o.desc}</p>
            </button>
          ))}
          <div>
            <Label>{action === 'requalify' ? 'Justification *' : 'Commentaire (optionnel)'}</Label>
            <Textarea value={justif} onChange={(e) => setJustif(e.target.value)} rows={2}
              data-testid="decision-justification" />
          </div>
          {error && <p className="text-sm text-red-600" data-testid="decision-error">{error}</p>}
          <Button className="w-full" disabled={!action || submitting || (action === 'requalify' && !justif.trim())}
            onClick={submit} data-testid="decision-submit-btn">
            {submitting ? 'Enregistrement...' : 'Appliquer la décision'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const DailyChecksTab = ({ onRefreshParc, sites = [] }) => {
  const [summary, setSummary] = useState(null);
  const [view, setView] = useState('today');
  const [checkVehicle, setCheckVehicle] = useState(null);
  const [checkRequiresReason, setCheckRequiresReason] = useState(false);
  const [decisionVehicle, setDecisionVehicle] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/fleet/daily/summary`, { withCredentials: true });
      setSummary(res.data);
    } catch (e) {
      console.error(e);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (!summary) return <p className="text-sm text-gray-500">Chargement...</p>;

  const cfg = summary.config || {};
  const openCheck = (v, requiresReason) => { setCheckRequiresReason(!!requiresReason); setCheckVehicle(v); };
  const outcomeBadge = (v) => {
    const m = DAILY_OUTCOME_META[v.latest_outcome];
    return m ? <span className={`text-xs px-2 py-1 rounded ${m.cls}`}>{m.label}</span> : null;
  };
  const controlBtn = (v, requiresReason) => summary.can_perform ? (
    <Button size="sm" onClick={() => openCheck(v, requiresReason)} data-testid={`daily-start-${v.vehicle_id}`}>
      <ClipboardCheck className="w-4 h-4 mr-1" /> Contrôler
    </Button>
  ) : null;

  return (
    <div className="space-y-4" data-testid="daily-checks-tab">
      <div className="flex gap-1 bg-gray-100 rounded-lg p-1 w-fit">
        <button type="button" onClick={() => setView('today')}
          className={`px-3 py-1.5 rounded text-sm font-medium ${view === 'today' ? 'bg-white shadow text-blue-700' : 'text-gray-500'}`}
          data-testid="daily-view-today-btn">Aujourd'hui</button>
        <button type="button" onClick={() => setView('history')}
          className={`px-3 py-1.5 rounded text-sm font-medium ${view === 'history' ? 'bg-white shadow text-blue-700' : 'text-gray-500'}`}
          data-testid="daily-view-history-btn">Historique quotidien</button>
      </div>

      {view === 'history' ? (
        <DailyHistoryView sites={sites} />
      ) : (
      <>
      <div className={`rounded-lg border p-3 text-sm ${cfg.effective ? 'bg-red-50 border-red-300 text-red-800' : 'bg-amber-50 border-amber-300 text-amber-800'}`}
        data-testid="daily-mode-banner">
        {cfg.effective ? (
          <p><strong>Mode obligatoire actif :</strong> les véhicules sans contrôle quotidien validé sont bloqués pour les missions (depuis le {cfg.enforced_at}).</p>
        ) : (
          <p><strong>Mode observation :</strong> le contrôle quotidien est visible mais ne bloque pas les missions
            {cfg.enforced_flag && cfg.config_error ? ` (⚠️ ${cfg.config_error})` : ''}
            {cfg.enforced_flag && !cfg.config_error && !cfg.effective ? ` (activation programmée : ${cfg.enforced_at})` : ''}.
            Seul l'état « En attente de décision sécurité » bloque toujours.</p>
        )}
      </div>

      <div className="flex items-center justify-between flex-wrap gap-2">
        <p className="text-sm text-gray-600">Journée du <strong>{summary.date}</strong> (heure de Paris) — contrôle rapide en 10 points, distinct du contrôle périodique complet (70 points).</p>
        {summary.can_perform && summary.next_todo && (
          <Button onClick={() => openCheck(summary.next_todo, false)} data-testid="daily-next-vehicle-btn">
            Contrôler le véhicule suivant <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        )}
      </div>

      {summary.attente_decision.length > 0 && (
        <div>
          <h3 className="font-semibold text-sm mb-2 text-purple-800">🛑 En attente de décision sécurité ({summary.attente_decision.length})</h3>
          <div className="space-y-2">
            {summary.attente_decision.map(v => (
              <Row key={v.vehicle_id} v={v}
                action={
                  <div className="flex gap-2">
                    {summary.is_fleet_manager && (
                      <Button size="sm" variant="destructive" onClick={() => setDecisionVehicle(v)} data-testid={`daily-decision-${v.vehicle_id}`}>
                        Prendre une décision
                      </Button>
                    )}
                    {summary.can_perform && v.recheck_requested && controlBtn(v, true)}
                  </div>
                } />
            ))}
          </div>
        </div>
      )}

      {summary.recontrole.length > 0 && (
        <div>
          <h3 className="font-semibold text-sm mb-2 text-orange-700">🔁 Re-contrôle requis après remise en service ({summary.recontrole.length})</h3>
          <div className="space-y-2">{summary.recontrole.map(v => <Row key={v.vehicle_id} v={v} action={controlBtn(v, true)} />)}</div>
        </div>
      )}

      <div>
        <h3 className="font-semibold text-sm mb-2">🔵 À contrôler aujourd'hui ({summary.a_faire.length})</h3>
        {summary.a_faire.length === 0 ? <p className="text-xs text-gray-400">Aucun véhicule</p> : (
          <div className="space-y-2">{summary.a_faire.map(v => <Row key={v.vehicle_id} v={v} action={controlBtn(v, v.has_validated)} />)}</div>
        )}
      </div>

      {summary.problemes.length > 0 && (
        <div>
          <h3 className="font-semibold text-sm mb-2 text-yellow-700">🟠 Contrôlés avec problème non bloquant ({summary.problemes.length})</h3>
          <div className="space-y-2">{summary.problemes.map(v => <Row key={v.vehicle_id} v={v} badge={outcomeBadge(v)} action={controlBtn(v, true)} />)}</div>
        </div>
      )}

      <div>
        <h3 className="font-semibold text-sm mb-2 text-green-700">🟢 Terminés aujourd'hui ({summary.termines.length})</h3>
        {summary.termines.length === 0 ? <p className="text-xs text-gray-400">Aucun véhicule</p> : (
          <div className="space-y-2">{summary.termines.map(v => <Row key={v.vehicle_id} v={v} badge={outcomeBadge(v)} action={controlBtn(v, true)} />)}</div>
        )}
      </div>

      {summary.exemptes.length > 0 && (
        <div>
          <h3 className="font-semibold text-sm mb-2 text-gray-500">⚙️ Exemptés (immobilisés / en réparation) ({summary.exemptes.length})</h3>
          <div className="space-y-2">{summary.exemptes.map(v => <Row key={v.vehicle_id} v={v} />)}</div>
        </div>
      )}
      </>
      )}

      <DailyCheckDialog vehicle={checkVehicle} requiresReason={checkRequiresReason}
        defaultReason={checkVehicle?.recheck_requested ? 'Re-contrôle demandé par le chef de parc' : ''}
        open={!!checkVehicle}
        onClose={() => { setCheckVehicle(null); load(); }}
        onValidated={() => { load(); onRefreshParc?.(); }} />
      <DecisionDialog vehicle={decisionVehicle} open={!!decisionVehicle}
        onClose={() => setDecisionVehicle(null)}
        onDone={() => { load(); onRefreshParc?.(); }} />
    </div>
  );
};

export default DailyChecksTab;
